import '../../../i18n';
import {useTranslation} from 'react-i18next';
import React, { useEffect, useMemo, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { MUTATION } from '../../../gql-operations';
import luckynoteLogo from '../../../assets/lucky_icon_title_small.svg';

const MESSAGE_TYPE = 'LUCKYNOTE_EXTENSION_AUTH_CODE';

function ExtensionAuthPage() {
    const {t: lnT} = useTranslation();

    const location = useLocation();
    const [status, setStatus] = useState('Connecting Luckynote extension...');
    const [error, setError] = useState('');
    const [connected, setConnected] = useState(false);
    const [createExtensionAuthCode] = useMutation(MUTATION.CREATE_EXTENSION_AUTH_CODE);
    const extensionId = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('extensionId') || '';
    }, [lnT, location.search]);

    useEffect(() => {
        if (!localStorage.getItem('token') || !extensionId) {
            return;
        }

        let cancelled = false;
        async function connectExtension() {
            try {
                const result = await createExtensionAuthCode({
                    variables: {
                        data: { extensionId }
                    }
                });
                const payload = result.data?.createExtensionAuthCode;
                if (payload?.__typename !== 'ExtensionAuthCodePayload' || !payload.code) {
                    throw new Error(payload?.message || 'Could not create an extension sign-in code.');
                }

                if (!window.chrome?.runtime?.sendMessage) {
                    throw new Error('Chrome extension messaging is not available in this browser.');
                }

                window.chrome.runtime.sendMessage(
                    extensionId,
                    { type: MESSAGE_TYPE, code: payload.code },
                    (response) => {
                        const runtimeError = window.chrome?.runtime?.lastError?.message;
                        if (cancelled) return;
                        if (runtimeError || !response?.success) {
                            setError(runtimeError || response?.error || lnT('auth:couldNotConnectTheExtension'));
                            return;
                        }
                        setConnected(true);
                        setStatus(lnT('auth:yourExtensionIsConnectedAndReadyTo'));
                    }
                );
            } catch (e) {
                if (!cancelled) {
                    setError(e.message || lnT('auth:couldNotConnectTheExtension'));
                }
            }
        }

        connectExtension();
        return () => {
            cancelled = true;
        };
    }, [createExtensionAuthCode, extensionId]);

    if (!extensionId) {
        return <Redirect to="/login" />;
    }

    if (!localStorage.getItem('token')) {
        const redirect = `/extension-auth${location.search}`;
        return <Redirect to={`/login?redirect=${encodeURIComponent(redirect)}`} />;
    }

    const showSuccess = connected && !error;

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.header}>
                    <img src={luckynoteLogo} alt={lnT('common:luckynote')} style={styles.logo} />
                </div>

                <h1 style={styles.heading}>
                    {showSuccess ? lnT('auth:connectedSuccessfully') : error ? lnT('auth:connectionFailed') : lnT('auth:connectingExtension')}
                </h1>

                <p style={styles.copy}>
                    {error || status}
                </p>

                {showSuccess && (
                    <div style={styles.actions}>
                        <a href="/home" style={styles.primaryAction}>{lnT('common:openLuckynote')}</a>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9f7',
        color: '#161a17',
        padding: 24,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
    },
    card: {
        width: '100%',
        maxWidth: 360,
        textAlign: 'center'
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 12,
        marginBottom: 22
    },
    logo: {
        width: 129,
        height: 24,
        objectFit: 'contain'
    },
    heading: {
        fontSize: 24,
        lineHeight: 1.15,
        letterSpacing: 0,
        margin: '0 0 8px'
    },
    copy: {
        fontSize: 15,
        lineHeight: 1.55,
        color: '#4a544d',
        margin: 0
    },
    actions: {
        display: 'flex',
        justifyContent: 'center',
        marginTop: 22
    },
    primaryAction: {
        borderRadius: 999,
        background: 'transparent',
        color: '#0f6b4f',
        textDecoration: 'none',
        padding: '9px 12px',
        fontWeight: 750,
        fontSize: 14
    }
};

export default ExtensionAuthPage;
